import moment from 'moment-timezone';
import { CalendarDates, CalendarViewData } from './types';

/** Non-padded YYYY-M-D key used in CalendarViewData maps. */
export const getDateKey = (date: Date, siteTimezone?: string): string => {
  if (siteTimezone) {
    const m = moment.tz(date, siteTimezone);
    return `${m.year()}-${m.month() + 1}-${m.date()}`;
  }

  return [date.getFullYear(), date.getMonth() + 1, date.getDate()].join('-');
};

export const toDateParam = (isoDate: string): string => isoDate.slice(0, 10);

/** YYYY-MM-DD (or ISO datetime) → non-padded YYYY-M-D. */
export const isoDateToDateKey = (iso: string): string => {
  const [y, m, d] = toDateParam(iso).split('-').map(Number);
  return `${y}-${m}-${d}`;
};

/**
 * Date key for a FullCalendar day-cell Date when `timeZone` is set on the calendar.
 * FC encodes each slot as UTC midnight with the calendar Y-M-D in UTC components;
 * converting that instant into site TZ would shift the day (e.g. Mon → Tue column).
 */
export const getFullCalendarDateKey = (date: Date): string =>
  isoDateToDateKey(moment.utc(date).format('YYYY-MM-DD'));

/** Day-of-month for a FullCalendar day-cell Date (see getFullCalendarDateKey). */
export const getFullCalendarDayNumber = (date: Date): number => {
  const dayPart = getFullCalendarDateKey(date).split('-')[2];
  return parseInt(dayPart, 10);
};

/** Non-padded YYYY-M-D → local-midnight Date (safe for display formatters). */
export const dateKeyToLocalDate = (dateKey: string): Date => {
  const [y, m, d] = dateKey.split('-').map(Number);
  return new Date(y, m - 1, d);
};

/** Midnight local time for a non-padded YYYY-M-D key — safe for ordering. */
export const dateKeyToTime = (dateKey: string): number => {
  const [y, m, d] = dateKey.split('-').map(Number);
  return new Date(y, m - 1, d).getTime();
};

export const compareDateKeys = (a: string, b: string): number =>
  dateKeyToTime(a) - dateKeyToTime(b);

/** True when dateKey falls within [start, end] inclusive (non-padded keys). */
export const isDateKeyInRange = (
  dateKey: string,
  start: string,
  end: string,
): boolean => {
  const t = dateKeyToTime(dateKey);
  return t >= dateKeyToTime(start) && t <= dateKeyToTime(end);
};

/** Maps currentUser.startOfWeek full-name strings to JS getDay() indices (0=Sun…6=Sat). */
const DAY_NAME_INDEX: Record<string, number> = {
  SUNDAY: 0,
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6,
};

/**
 * Returns the JS getDay() index for the user's configured week-start day.
 * Defaults to Sunday (0) when null or unrecognised.
 */
export const parseStartOfWeekIndex = (startOfWeek: string | null): number =>
  DAY_NAME_INDEX[startOfWeek?.toUpperCase() ?? ''] ?? 0;

/** Date key for the week-start day (per user preference) that contains date. */
export const weekStartDateKeyFor = (
  date: Date,
  weekStartDayIndex: number,
  siteTimezone?: string,
): string => {
  if (siteTimezone) {
    const m = moment.tz(date, siteTimezone).startOf('day');
    const offset = (m.day() - weekStartDayIndex + 7) % 7;
    m.subtract(offset, 'days');
    return `${m.year()}-${m.month() + 1}-${m.date()}`;
  }

  const offset = (date.getDay() - weekStartDayIndex + 7) % 7;
  const weekStart = new Date(date);
  weekStart.setDate(date.getDate() - offset);
  return getDateKey(weekStart);
};

/** Week-start key from an existing non-padded date key (YYYY-M-D). */
export const weekStartDateKeyFromDateKey = (
  dateKey: string,
  weekStartDayIndex: number,
  siteTimezone: string,
): string => {
  const m = moment.tz(dateKey, 'YYYY-M-D', siteTimezone).startOf('day');
  const offset = (m.day() - weekStartDayIndex + 7) % 7;
  m.subtract(offset, 'days');
  return `${m.year()}-${m.month() + 1}-${m.date()}`;
};

export const getRoleGroupList = (
  listOfIds: number[],
  roleGroups: mumms.RoleGroup[],
): string =>
  listOfIds
    .map((rgId) => {
      if (rgId === 0) {
        return 'All';
      }

      const roleGroup = roleGroups.find((rg) => rg.id === rgId);
      return roleGroup ? roleGroup.name : '';
    })
    .join(', ');

export const getRoleGroupsForFilter = (
  frequencies: mumms.patientCarePlanProblemFrequency[],
  roleGroups: mumms.RoleGroup[],
): mumms.RoleGroup[] => {
  const roleGroupIds = [
    ...new Set(frequencies.map((frequency) => frequency.roleGroupId)),
  ];

  return roleGroupIds
    .map((roleGroupId) => roleGroups.find((rg) => rg.id === roleGroupId))
    .filter((roleGroup): roleGroup is mumms.RoleGroup => !!roleGroup);
};

export const getFrequencyDotTooltip = (
  roleName: string,
  frequencyLabel: string,
): string => `${roleName} ${frequencyLabel}`.trim();

/**
 * Two-letter role group abbreviations shown on calendar dots and plan boxes,
 * keyed by the role group name with spaces / punctuation removed and lower
 * cased (authproxy names such as "Social Worker" and the local "SocialWorker"
 * catalogue both resolve). CPVF also renames the therapy groups to
 * "Sp. Therapy" / "Ph. Therapy" / "Oc. Therapy", so those short labels are
 * listed too. Names not listed fall back to their first two letters
 * (Nurse → NU, Volunteer → VO). Spiritual is CL (chaplain), not SP.
 */
export const ROLE_GROUP_ABBREVIATIONS: Record<string, string> = {
  socialworker: 'SW',
  speechtherapy: 'ST',
  speechtherapist: 'ST',
  sptherapy: 'ST',
  physicaltherapy: 'PT',
  physicaltherapist: 'PT',
  phtherapy: 'PT',
  occupationaltherapy: 'OT',
  occupationaltherapist: 'OT',
  octherapy: 'OT',
  aide: 'AD',
  massagetherapy: 'MT',
  massagetherapist: 'MT',
  bereavement: 'BV',
  doctor: 'DR',
  homemaker: 'HM',
  mentalhealth: 'MH',
  mentalhealthcounseling: 'MH',
  mentalhealthcounselor: 'MH',
  spiritual: 'CL',
  chaplain: 'CL',
};

export const roleGroupAbbreviation = (roleGroupName: string): string => {
  const key = (roleGroupName ?? '').toLowerCase().replace(/[^a-z]/g, '');
  return (
    ROLE_GROUP_ABBREVIATIONS[key] ?? roleGroupName.slice(0, 2).toUpperCase()
  );
};

/** e.g. `3x Daily`, `4x Monthly` — built from CPF frequency count + period type. */
export const formatVisitFrequencyLabel = (
  frequency: number,
  period: string,
): string => {
  if (!period) return '';
  if (!frequency) return period;
  return `${frequency}x ${period}`;
};

export const createEmptyCalendarViewData = (
  calendarDates: CalendarDates,
  siteTimezone: string,
): CalendarViewData => ({
  siteTimezone,
  rangeStart: calendarDates[0],
  rangeEnd: calendarDates[1],
  days: {},
  weeks: {},
});

export const getCalendarDayData = (
  viewData: CalendarViewData | null | undefined,
  date: Date,
) => {
  if (!viewData) {
    return null;
  }

  return viewData.days[getFullCalendarDateKey(date)] ?? null;
};
